import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { Link } from 'react-router-dom';
import PhotoUploadField from '../../components/feature/PhotoUploadField';

interface ServiceCategory {
  id: string;
  name: string;
  icon: string;
  subcategories: Array<{ id: string; name: string; isOther?: boolean }>;
}

// Van size configuration
interface VanSize {
  id: string;
  label: string;
  rate: number;
  icon: string;
}

const VAN_SIZES: VanSize[] = [
  { id: 'small', label: 'Small Van', rate: 1.50, icon: 'ri-truck-line' },
  { id: 'medium', label: 'Medium Van', rate: 1.60, icon: 'ri-truck-line' },
  { id: 'large', label: 'Large Van', rate: 1.70, icon: 'ri-truck-line' },
  { id: 'xl', label: 'Extra-Large Van', rate: 1.90, icon: 'ri-truck-line' }
];

const ServiceRequestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedProvider = searchParams.get('provider');
  const preSelectedTrade = searchParams.get('trade');
  const preSelectedPostcode = searchParams.get('postcode');
  const categoryParam = searchParams.get('category');
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<string>(preSelectedProvider || '');
  const [providerInfo, setProviderInfo] = useState<any>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: preSelectedPostcode || '',
    description: '',
    jobTitle: '',
    scheduledDate: '',
    scheduledTime: '',
    isInstant: true,
    pickupAddress: '',
    dropoffAddress: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [coordinates, setCoordinates] = useState({ lat: 0, lng: 0 });
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPostcodeValid, setIsPostcodeValid] = useState(true);
  
  // Transportation & Moving states
  const [selectedVanSize, setSelectedVanSize] = useState<string>('');
  const [pickupPostcode, setPickupPostcode] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffPostcode, setDropoffPostcode] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [distance, setDistance] = useState<number>(0);
  const [loadingHelp, setLoadingHelp] = useState(false);
  const [offloadingHelp, setOffloadingHelp] = useState(false);
  const [multiDrop, setMultiDrop] = useState(false);
  const [transportStep, setTransportStep] = useState(1);
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [distanceError, setDistanceError] = useState('');
  const [proceedingToCheckout, setProceedingToCheckout] = useState(false);
  const [urgency, setUrgency] = useState('asap');
  const [scheduledTime, setScheduledTime] = useState('');
  const [description, setDescription] = useState('');

  const categories: ServiceCategory[] = [
    {
      id: 'handyman',
      name: 'Handyman Services',
      icon: 'ri-tools-fill',
      subcategories: [
        { id: 'furniture-assembly', name: 'Furniture assembly' },
        { id: 'tv-mounting', name: 'TV mounting' },
        { id: 'curtain-rails-blinds', name: 'Curtain rails & blinds fitting' },
        { id: 'shelves-wall-mounting', name: 'Shelves & wall mounting' },
        { id: 'door-repairs', name: 'Door repairs & handles' },
        { id: 'flat-pack-assembly', name: 'Flat-pack assembly' },
        { id: 'picture-mirror-hanging', name: 'Picture & mirror hanging' },
        { id: 'plaster-repairs', name: 'Minor plaster repairs' },
        { id: 'silicone-sealing', name: 'Silicone sealing (bath/kitchen)' },
        { id: 'garden-tidy', name: 'Garden tidy-up / small jobs' },
        { id: 'general-repairs', name: 'General home repairs' },
        { id: 'odd-jobs', name: 'Odd jobs (small fixes)' },
        { id: 'other', name: 'Other – Describe your job', isOther: true }
      ]
    },
    {
      id: 'plumbing',
      name: 'Plumbing Services',
      icon: 'ri-drop-fill',
      subcategories: [
        { id: 'leaking-tap', name: 'Leaking tap repair' },
        { id: 'blocked-drain', name: 'Blocked sink/drain' },
        { id: 'toilet-repair', name: 'Toilet repair / running toilet' },
        { id: 'shower-repair', name: 'Shower repair' },
        { id: 'radiator-bleeding', name: 'Radiator bleeding / fixes' },
        { id: 'pipe-leak', name: 'Pipe leak / burst pipe' },
        { id: 'replace-taps', name: 'Replace taps' },
        { id: 'install-appliance', name: 'Install washing machine / dishwasher' },
        { id: 'bathroom-plumbing', name: 'Bathroom plumbing repairs' },
        { id: 'boiler-repair', name: 'Boiler repair' },
        { id: 'boiler-service', name: 'Boiler service' },
        { id: 'install-radiator', name: 'Install/replace radiator' },
        { id: 'other', name: 'Other – Describe your job', isOther: true }
      ]
    },
    {
      id: 'electrical',
      name: 'Electrical Services',
      icon: 'ri-flashlight-fill',
      subcategories: [
        { id: 'replace-light', name: 'Replace light fitting' },
        { id: 'install-light', name: 'Install light fitting' },
        { id: 'socket-replacement', name: 'Socket replacement' },
        { id: 'add-socket', name: 'Add new socket' },
        { id: 'fuse-box-check', name: 'Fuse box / consumer unit check' },
        { id: 'safety-check', name: 'Electrical safety check (EICR)' },
        { id: 'tripping-electrics', name: 'Tripping electrics fault' },
        { id: 'extractor-fan', name: 'Install extractor fan' },
        { id: 'cooker-connection', name: 'Install cooker / hob connection' },
        { id: 'doorbell', name: 'Doorbell installation' },
        { id: 'smoke-alarm', name: 'Smoke alarm installation' },
        { id: 'outdoor-light', name: 'Outdoor/security light install' },
        { id: 'other', name: 'Other – Describe your job', isOther: true }
      ]
    },
    {
      id: 'cleaning',
      name: 'Cleaning Services',
      icon: 'ri-brush-fill',
      subcategories: [
        { id: 'regular-cleaning', name: 'Regular home cleaning' },
        { id: 'deep-cleaning', name: 'Deep cleaning' },
        { id: 'tenancy-cleaning', name: 'End of tenancy cleaning' },
        { id: 'one-off-clean', name: 'One-off clean' },
        { id: 'oven-cleaning', name: 'Oven cleaning' },
        { id: 'carpet-cleaning', name: 'Carpet cleaning' },
        { id: 'bathroom-deep', name: 'Bathroom deep clean' },
        { id: 'kitchen-deep', name: 'Kitchen deep clean' },
        { id: 'after-builders', name: 'After builders cleaning' },
        { id: 'move-in-cleaning', name: 'Move-in cleaning' },
        { id: 'window-cleaning', name: 'Window cleaning' },
        { id: 'declutter-tidy', name: 'Declutter / tidy-up (light)' },
        { id: 'other', name: 'Other – Describe your job', isOther: true }
      ]
    },
    {
      id: 'waste-removal',
      name: 'Waste Removal',
      icon: 'ri-delete-bin-fill',
      subcategories: [
        { id: 'household-waste', name: 'Household waste removal' },
        { id: 'garden-waste', name: 'Garden waste removal' },
        { id: 'furniture-disposal', name: 'Furniture disposal' },
        { id: 'appliance-removal', name: 'Appliance removal' },
        { id: 'diy-rubble', name: 'DIY rubble / renovation waste' },
        { id: 'garage-clearance', name: 'Garage clearance' },
        { id: 'loft-clearance', name: 'Loft clearance' },
        { id: 'office-clearance', name: 'Office clearance' },
        { id: 'mattress-removal', name: 'Mattress removal' },
        { id: 'shed-removal', name: 'Shed removal' },
        { id: 'single-item', name: 'Single item pickup' },
        { id: 'full-load', name: 'Full load clearance' },
        { id: 'other', name: 'Other – Describe your job', isOther: true }
      ]
    },
    {
      id: 'transport-moving',
      name: 'Transportation & Moving',
      icon: 'ri-truck-fill',
      subcategories: [
        { id: 'man-van-small', name: 'Man & van small move' },
        { id: 'single-item-delivery', name: 'Single item delivery' },
        { id: 'furniture-delivery', name: 'Furniture delivery' },
        { id: 'marketplace-pickup', name: 'Marketplace pickup & delivery' },
        { id: 'small-flat-move', name: 'Home move (small flat)' },
        { id: 'house-move', name: 'Home move (house)' },
        { id: 'office-move', name: 'Office move' },
        { id: 'student-move', name: 'Student move' },
        { id: 'storage-pickup', name: 'Storage pickup/drop-off' },
        { id: 'heavy-item', name: 'Heavy item move (washing machine, sofa)' },
        { id: 'pack-move-help', name: 'Pack & move help' },
        { id: 'same-day-courier', name: 'Same-day courier' },
        { id: 'other', name: 'Other – Describe your job', isOther: true }
      ]
    }
  ];

  // Initialize category from URL parameter
  useEffect(() => {
    if (categoryParam) {
      const categoryMap: Record<string, string> = {
        'plumber': 'plumbing',
        'plumbing': 'plumbing',
        'electrician': 'electrical',
        'electrical': 'electrical',
        'cleaner': 'cleaning',
        'cleaning': 'cleaning',
        'handyman': 'handyman',
        'waste': 'waste-removal',
        'waste-removal': 'waste-removal',
        'transport': 'transport-moving',
        'transport-moving': 'transport-moving',
        'transportation': 'transport-moving'
      };

      const mappedId = categoryMap[categoryParam.toLowerCase()] || categoryParam;
      setSelectedCategory(mappedId);
      
      // REMOVED: Auto-select subcategory logic
      // Now ALL categories will show service selection screen
    }
  }, [categoryParam]);

  useEffect(() => {
    if (preSelectedProvider) {
      loadProviderInfo(preSelectedProvider);
    }
  }, [preSelectedProvider]);

  useEffect(() => {
    if (preSelectedTrade) {
      setFormData(prev => ({ ...prev, serviceType: preSelectedTrade }));
    }
  }, [preSelectedTrade]);

  const [additionalStops, setAdditionalStops] = useState<Array<{
    id: string;
    postcode: string;
    city: string;
    distance: number;
    cost: number;
  }>>([]);
  const [showAddStop, setShowAddStop] = useState(false);
  const [newStopPostcode, setNewStopPostcode] = useState('');
  const [newStopCity, setNewStopCity] = useState('');
  const [isCalculatingStop, setIsCalculatingStop] = useState(false);

  const loadProviderInfo = async () => {
    try {
      const { data } = await supabase
        .from('provider_profiles')
        .select(`
          id,
          business_name,
          trade_type,
          rating,
          total_jobs,
          users (
            full_name
          )
        `)
        .eq('id', preSelectedProvider)
        .single();

      if (data) {
        setProviderInfo(data);
      }
    } catch (error) {
      console.error('Error loading provider:', error);
    }
  };

  const handleSubcategorySelect = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId);
    if (selectedCategory === 'transport-moving') {
      setTransportStep(2);
    }
  };

  const handleSubmit = async (formData: any) => {
    setError('');
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Please sign in to request a service.');
        setLoading(false);
        setTimeout(() => {
          window.REACT_APP_NAVIGATE('/auth/signin');
        }, 2000);
        return;
      }

      if (!formData.name || formData.name.trim().length < 2) {
        setError('Please enter your full name.');
        setLoading(false);
        return;
      }

      if (!formData.email || !formData.email.includes('@')) {
        setError('Please enter a valid email address.');
        setLoading(false);
        return;
      }

      if (!formData.phone || formData.phone.trim().length < 10) {
        setError('Please enter a valid phone number.');
        setLoading(false);
        return;
      }

      if (!formData.address || formData.address.trim().length < 5) {
        setError('Please enter a complete address.');
        setLoading(false);
        return;
      }

      if (!formData.postcode || formData.postcode.trim().length < 3) {
        setError('Please enter a valid postcode.');
        setLoading(false);
        return;
      }

      if (currentSubcategory?.isOther) {
        if (!formData.jobTitle || formData.jobTitle.trim().length < 5) {
          setError('Please enter a job title (at least 5 characters).');
          setLoading(false);
          return;
        }
        if (formData.jobTitle.trim().length > 60) {
          setError('Job title must be 60 characters or less.');
          setLoading(false);
          return;
        }
      }

      if (!formData.description || formData.description.trim().length < 10) {
        setError('Please provide more details about your service request (at least 10 characters).');
        setLoading(false);
        return;
      }

      if (!formData.isInstant && formData.scheduledDate) {
        const selectedDate = new Date(formData.scheduledDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
          setError('Please select a future date for your service.');
          setLoading(false);
          return;
        }
      }

      setCheckingAvailability(true);

      const postcodeResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ postcode: formData.postcode }),
        }
      );

      setCheckingAvailability(false);

      if (!postcodeResponse.ok) {
        setError('Unable to verify your postcode. Please check and try again.');
        setLoading(false);
        return;
      }

      const postcodeData = await postcodeResponse.json();
      
      if (!postcodeData.valid) {
        setError(postcodeData.message || 'We don\'t currently cover your area — expanding soon');
        setLoading(false);
        return;
      }

      let scheduledDateTime = null;
      if (!formData.isInstant && formData.scheduledDate && formData.scheduledTime) {
        scheduledDateTime = `${formData.scheduledDate}T${formData.scheduledTime}:00`;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-job-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            userId: user.id,
            categoryId: selectedCategory,
            subcategoryId: selectedSubcategory,
            jobTitle: formData.jobTitle || null,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            postcode: formData.postcode,
            scheduledDate: scheduledDateTime,
            isInstant: formData.isInstant,
            description: formData.description,
            images: uploadedPhotos,
            coordinates: postcodeData.coordinates || { lat: 0, lng: 0 },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (data.error?.includes('No providers available')) {
          setError('No providers available in your area right now. We\'ve saved your request and will notify you when a provider becomes available.');
          setLoading(false);
          
          if (data.jobId) {
            setTimeout(() => {
              window.REACT_APP_NAVIGATE(`/job-tracking?id=${data.jobId}`);
            }, 3000);
          }
          return;
        } else {
          setError(data.error || 'Unable to create service request. Please try again.');
          setLoading(false);
          return;
        }
      }

      if (data.jobId) {
        setSuccess(`Service request created successfully! ${data.providersNotified || 0} providers notified.`);
        
        setTimeout(() => {
          window.REACT_APP_NAVIGATE(`/job-tracking?id=${data.jobId}`);
        }, 2000);
      } else {
        setError('Service request created but tracking unavailable. Please check your dashboard.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Service request error:', err);
      setError('Connection error. Please check your internet and try again.');
      setLoading(false);
    }
  };

  const currentCategory = categories.find(c => c.id === selectedCategory);
  const currentSubcategory = currentCategory?.subcategories.find(s => s.id === selectedSubcategory);

  useEffect(() => {
    document.title = 'Book a Service | Instant Home Services UK - Handyman, Plumber, Electrician & More';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Book instant home services in the UK. Get same-day handyman, plumbing, electrical, cleaning, waste removal & transport services. Live tracking, secure payments, verified providers across London, Birmingham, Manchester & more.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Book instant home services in the UK. Get same-day handyman, plumbing, electrical, cleaning, waste removal & transport services. Live tracking, secure payments, verified providers across London, Birmingham, Manchester & more.';
      document.head.appendChild(meta);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'book service, instant booking, same-day service, handyman booking, plumber booking, electrician booking, cleaner booking, waste removal, transport service, home services UK');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'book service, instant booking, same-day service, handyman booking, plumber booking, electrician booking, cleaner booking, waste removal, transport service, home services UK';
      document.head.appendChild(meta);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://taskbuddy.co.uk/service-request');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://taskbuddy.co.uk/service-request';
      document.head.appendChild(link);
    }
  }, []);

  // Calculate transportation distance
  const calculateTransportationDistance = async () => {
    if (!pickupPostcode || !dropoffPostcode) {
      setDistanceError('Please enter both postcodes');
      return;
    }
    
    setIsCalculatingDistance(true);
    setDistanceError('');
    
    try {
      const pickupResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ postcode: pickupPostcode }),
        }
      );
      
      const dropoffResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ postcode: dropoffPostcode, skipCityCheck: true }),
        }
      );
      
      if (!pickupResponse.ok || !dropoffResponse.ok) {
        setDistanceError('Unable to validate postcodes. Please check and try again.');
        setIsCalculatingDistance(false);
        return;
      }

      const pickupData = await pickupResponse.json();
      const dropoffData = await dropoffResponse.json();
      
      if (!pickupData.valid) {
        setDistanceError('Invalid pickup postcode. Please check and try again.');
        setIsCalculatingDistance(false);
        return;
      }
      
      if (!dropoffData.valid) {
        setDistanceError('Invalid drop-off postcode. Please check and try again.');
        setIsCalculatingDistance(false);
        return;
      }
      
      // Calculate distance using Haversine formula
      const lat1 = pickupData.coordinates.lat;
      const lon1 = pickupData.coordinates.lng;
      const lat2 = dropoffData.coordinates.lat;
      const lon2 = dropoffData.coordinates.lng;
      
      const R = 3959; // Earth's radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const calculatedDistance = R * c;
      
      setDistance(Math.round(calculatedDistance * 10) / 10);
      setDistanceError('');
      
    } catch (err) {
      console.error('Distance calculation error:', err);
      setDistanceError('Unable to calculate distance. Please try again.');
    } finally {
      setIsCalculatingDistance(false);
    }
  };

  // Auto-calculate distance when both postcodes are entered
  useEffect(() => {
    if (pickupPostcode && dropoffPostcode && pickupPostcode.length >= 5 && dropoffPostcode.length >= 5) {
      const timer = setTimeout(() => {
        calculateTransportationDistance();
      }, 1000); // Debounce for 1 second
      
      return () => clearTimeout(timer);
    } else {
      setDistance(0);
      setDistanceError('');
    }
  }, [pickupPostcode, dropoffPostcode]);

  // Calculate price breakdown
  const calculatePriceBreakdown = () => {
    if (!selectedVanSize || distance === 0) return null;
    
    const selectedVan = VAN_SIZES.find(v => v.id === selectedVanSize);
    if (!selectedVan) return null;
    
    const BASE_FEE = 50;
    const PLATFORM_FEE_RATE = 0.10;
    const VAT_RATE = 0.20;
    
    const mileageCost = distance * selectedVan.rate;
    const loadingCost = loadingHelp ? 25 : 0;
    const offloadingCost = offloadingHelp ? 25 : 0;
    const extrasTotal = loadingCost + offloadingCost;
    
    const subtotal = BASE_FEE + mileageCost + extrasTotal;
    const platformFee = subtotal * PLATFORM_FEE_RATE;
    const preVatTotal = subtotal + platformFee;
    const vat = preVatTotal * VAT_RATE;
    const finalTotal = preVatTotal + vat;
    
    return {
      baseFee: BASE_FEE,
      distance,
      rate: selectedVan.rate,
      mileageCost,
      loadingCost,
      offloadingCost,
      extrasTotal,
      subtotal,
      platformFee,
      preVatTotal,
      vat,
      finalTotal
    };
  };

  const priceBreakdown = calculatePriceBreakdown();

  // Validation function for transport step 2 - SIMPLIFIED
  const isTransportStep2Valid = () => {
    return (
      selectedVanSize &&
      pickupPostcode.trim().length >= 5 &&
      pickupAddress.trim().length >= 5 &&
      dropoffPostcode.trim().length >= 5 &&
      dropoffAddress.trim().length >= 5 &&
      distance > 0 &&
      !isCalculatingDistance
    );
  };

  // Handle continue to extras - SIMPLIFIED
  const handleContinueToExtras = () => {
    setError('');
    
    if (!selectedVanSize) {
      setError('⚠️ Please select a van size');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!pickupPostcode || pickupPostcode.trim().length < 5) {
      setError('⚠️ Please enter pickup postcode');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!pickupAddress || pickupAddress.trim().length < 5) {
      setError('⚠️ Please enter pickup address');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!dropoffPostcode || dropoffPostcode.trim().length < 5) {
      setError('⚠️ Please enter drop-off postcode');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!dropoffAddress || dropoffAddress.trim().length < 5) {
      setError('⚠️ Please enter drop-off address');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (isCalculatingDistance) {
      setError('⏳ Calculating distance, please wait...');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (distance === 0) {
      setError('⏳ Distance not calculated yet. Please wait or click "Calculate Distance" button.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // All good - proceed
    setTransportStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle transportation checkout
  const handleTransportationCheckout = async () => {
    if (!priceBreakdown) return;
    
    setProceedingToCheckout(true);
    setError('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Please sign in to proceed with booking.');
        setProceedingToCheckout(false);
        setTimeout(() => {
          window.REACT_APP_NAVIGATE('/auth/signin');
        }, 2000);
        return;
      }

      // Create job request first
      const jobResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-job-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            userId: user.id,
            categoryId: selectedCategory,
            subcategoryId: selectedSubcategory,
            jobTitle: `${VAN_SIZES.find(v => v.id === selectedVanSize)?.label} - ${currentSubcategory?.name}`,
            name: fullName,
            email: email,
            phone: phone,
            address: `${pickupAddress} to ${dropoffAddress}`,
            postcode: pickupPostcode,
            scheduledDate: urgency === 'scheduled' && scheduledTime ? scheduledTime : null,
            isInstant: urgency === 'asap',
            description: description || `Transportation service: ${distance} miles, ${loadingHelp ? 'with loading help, ' : ''}${offloadingHelp ? 'with off-loading help, ' : ''}${multiDrop ? 'multi-drop enabled' : ''}`,
            images: [],
            coordinates: { lat: 0, lng: 0 },
            transportationDetails: {
              vanSize: selectedVanSize,
              pickupPostcode,
              pickupAddress,
              dropoffPostcode,
              dropoffAddress,
              distance,
              loadingHelp,
              offloadingHelp,
              multiDrop,
              priceBreakdown
            }
          }),
        }
      );

      const jobData = await jobResponse.json();

      if (!jobResponse.ok) {
        setError(jobData.error || 'Unable to create booking. Please try again.');
        setProceedingToCheckout(false);
        return;
      }

      // Create deposit payment (advance payment)
      const depositAmount = priceBreakdown.finalTotal * 0.3; // 30% deposit
      
      const paymentResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-deposit-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            jobId: jobData.jobId,
            amount: Math.round(depositAmount * 100), // Convert to cents
            totalAmount: Math.round(priceBreakdown.finalTotal * 100)
          }),
        }
      );

      const paymentData = await paymentResponse.json();

      if (!paymentResponse.ok) {
        setError(paymentData.error || 'Unable to create payment. Please try again.');
        setProceedingToCheckout(false);
        return;
      }

      // Redirect to Stripe checkout
      if (paymentData.url) {
        window.location.href = paymentData.url;
      } else {
        setError('Payment URL not received. Please try again.');
        setProceedingToCheckout(false);
      }

    } catch (err) {
      console.error('Checkout error:', err);
      setError('Connection error. Please check your internet and try again.');
      setProceedingToCheckout(false);
    }
  };

  const handleAddStop = async () => {
    if (!newStopPostcode.trim() || !newStopCity.trim()) {
      setError('Please enter both postcode and city');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!selectedVanSize || !dropoffPostcode) {
      setError('Please complete the initial journey first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setIsCalculatingStop(true);
    setError('');

    try {
      // Validate the new stop postcode first (skip city check for UK-wide service)
      const newStopResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ postcode: newStopPostcode.trim(), skipCityCheck: true }),
        }
      );

      const newStopData = await newStopResponse.json();

      if (!newStopData.valid) {
        setError('Invalid postcode for the new stop. Please check and try again.');
        setTimeout(() => setError(''), 3000);
        setIsCalculatingStop(false);
        return;
      }

      // Get the last destination postcode (either the last additional stop or the original dropoff)
      const lastDestinationPostcode = additionalStops.length > 0 
        ? additionalStops[additionalStops.length - 1].postcode
        : dropoffPostcode;

      // Get coordinates for the last destination
      const lastDestResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ postcode: lastDestinationPostcode, skipCityCheck: true }),
        }
      );

      const lastDestData = await lastDestResponse.json();

      if (!lastDestData.valid) {
        setError('Error calculating distance from last location');
        setTimeout(() => setError(''), 3000);
        setIsCalculatingStop(false);
        return;
      }

      // Calculate distance using Haversine formula
      const lat1 = lastDestData.coordinates.lat;
      const lon1 = lastDestData.coordinates.lng;
      const lat2 = newStopData.coordinates.lat;
      const lon2 = newStopData.coordinates.lng;

      const R = 3959; // Earth's radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const stopDistance = R * c;

      const selectedVan = VAN_SIZES.find(v => v.id === selectedVanSize);
      const stopCost = stopDistance * (selectedVan?.rate || 0);

      const newStop = {
        id: Date.now().toString(),
        postcode: newStopPostcode.trim().toUpperCase(),
        city: newStopCity.trim(),
        distance: Math.round(stopDistance * 10) / 10,
        cost: Math.round(stopCost * 100) / 100,
      };

      setAdditionalStops([...additionalStops, newStop]);
      setNewStopPostcode('');
      setNewStopCity('');
      setShowAddStop(false);
      setSuccess('Stop added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error adding stop:', error);
      setError('Failed to add stop. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsCalculatingStop(false);
    }
  };

  const handleRemoveStop = (stopId: string) => {
    setAdditionalStops(additionalStops.filter(stop => stop.id !== stopId));
  };

  const calculateTotalWithStops = () => {
    if (!priceBreakdown) return 0;
    const stopsTotal = additionalStops.reduce((sum, stop) => sum + stop.cost, 0);
    
    // Recalculate with additional mileage
    const selectedVan = VAN_SIZES.find(v => v.id === selectedVanSize);
    if (!selectedVan) return priceBreakdown.finalTotal;
    
    const BASE_FEE = 50;
    const PLATFORM_FEE_RATE = 0.10;
    const VAT_RATE = 0.20;
    
    const totalMileageCost = priceBreakdown.mileageCost + stopsTotal;
    const loadingCost = loadingHelp ? 25 : 0;
    const offloadingCost = offloadingHelp ? 25 : 0;
    const extrasTotal = loadingCost + offloadingCost;
    
    const subtotal = BASE_FEE + totalMileageCost + extrasTotal;
    const platformFee = subtotal * PLATFORM_FEE_RATE;
    const preVatTotal = subtotal + platformFee;
    const vat = preVatTotal * VAT_RATE;
    const finalTotal = preVatTotal + vat;
    
    return finalTotal;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/dc77c2be5a78759d16f1acf02198b697.png" 
                alt="TaskBudy" 
                className="h-8 md:h-10"
              />
            </Link>
            <button
              onClick={() => navigate('/')}
              className="px-3 sm:px-4 py-2 text-sm text-gray-700 hover:text-[#0B3C5D] transition-colors whitespace-nowrap cursor-pointer font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Error Messages - Fixed Position - Mobile Optimized */}
        {error && (
          <div className="fixed top-20 right-4 left-4 md:left-auto md:right-4 md:w-96 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-lg z-50 animate-slideIn">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-xl"></i>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="ml-3 flex-shrink-0 text-red-500 hover:text-red-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="fixed top-20 right-4 left-4 md:left-auto md:right-4 md:w-96 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg shadow-lg z-50 animate-slideIn">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <i className="ri-checkbox-circle-line text-green-500 text-xl"></i>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 lg:p-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Request a Service</h1>
            <p className="text-sm sm:text-base text-gray-600 mb-6">Fill in the details and we'll match you with the best provider</p>
          </div>

          {providerInfo && (
            <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-white text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">{providerInfo.business_name}</h3>
                    <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded whitespace-nowrap">
                      Favorite Provider
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 flex-wrap">
                    <span className="truncate">{providerInfo.trade_type}</span>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                      <span>{providerInfo.rating.toFixed(1)}</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <span className="whitespace-nowrap">{providerInfo.total_jobs} jobs</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Service Selection - Mobile Optimized */}
          {selectedCategory && !selectedSubcategory && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Select Service</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Choose the specific service you need</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentCategory?.subcategories.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => handleSubcategorySelect(sub.id)}
                      className="p-3 sm:p-4 border-2 border-gray-200 rounded-lg hover:border-[#0B3C5D] hover:bg-[#0B3C5D]/5 transition-all text-left cursor-pointer"
                    >
                      <div className="font-semibold text-sm sm:text-base text-gray-900">{sub.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Transportation Steps 2-4 - Mobile Optimized */}
          {selectedCategory === 'transport-moving' && selectedSubcategory && (
            <div className="space-y-6">
              {/* Progress Steps - Mobile Optimized */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center justify-between">
                  {[1, 2, 3, 4].map((s, index) => (
                    <div key={s} className="flex items-center flex-1">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                        transportStep >= s ? 'bg-[#0B3C5D] text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {s}
                      </div>
                      {index < 3 && (
                        <div className={`flex-1 h-1 mx-1 ${
                          transportStep > s ? 'bg-[#0B3C5D]' : 'bg-gray-200'
                        }`}></div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-[10px] sm:text-xs">
                  <span className={transportStep === 1 ? 'font-semibold text-[#0B3C5D]' : 'text-gray-600'}>Service</span>
                  <span className={transportStep === 2 ? 'font-semibold text-[#0B3C5D]' : 'text-gray-600'}>Van & Location</span>
                  <span className={transportStep === 3 ? 'font-semibold text-[#0B3C5D]' : 'text-gray-600'}>Extras</span>
                  <span className={transportStep === 4 ? 'font-semibold text-[#0B3C5D]' : 'text-gray-600'}>Summary</span>
                </div>
              </div>

              {/* Step 2: Van Size & Location - Mobile Optimized */}
              {transportStep === 2 && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Select Van Size & Location</h2>
                    <p className="text-sm sm:text-base text-gray-600">Choose the van size and enter your pickup and drop-off locations</p>
                  </div>

                  {/* Van Size Selection - Mobile Optimized */}
                  <div className="mb-8">
                    <label className="block text-sm font-semibold text-gray-900 mb-4">
                      Van Size <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {[
                        { id: 'small', name: 'Small Van', icon: 'ri-truck-line', rate: '£1.50 per mile', size: 'text-2xl sm:text-3xl' },
                        { id: 'medium', name: 'Medium Van', icon: 'ri-truck-line', rate: '£1.60 per mile', size: 'text-3xl sm:text-4xl' },
                        { id: 'large', name: 'Large Van', icon: 'ri-truck-line', rate: '£1.70 per mile', size: 'text-4xl sm:text-5xl' },
                        { id: 'xl', name: 'Extra-Large Van', icon: 'ri-truck-line', rate: '£1.90 per mile', size: 'text-5xl sm:text-6xl' }
                      ].map((van) => (
                        <button
                          key={van.id}
                          type="button"
                          onClick={() => setSelectedVanSize(van.id)}
                          className={`p-3 sm:p-4 lg:p-6 rounded-xl border-2 transition-all duration-200 hover:shadow-lg cursor-pointer ${
                            selectedVanSize === van.id
                              ? 'border-[#0B3C5D] bg-[#0B3C5D]/10 shadow-md'
                              : 'border-gray-200 hover:border-[#0B3C5D]/50'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center">
                            <div className={`${van.size} mb-2 ${selectedVanSize === van.id ? 'text-[#0B3C5D]' : 'text-gray-700'}`}>
                              <i className={van.icon}></i>
                            </div>
                            <div className={`font-semibold text-xs sm:text-sm lg:text-base mb-1 ${selectedVanSize === van.id ? 'text-[#0B3C5D]' : 'text-gray-900'}`}>
                              {van.name}
                            </div>
                            <div className={`text-[10px] sm:text-xs lg:text-sm ${selectedVanSize === van.id ? 'text-[#0B3C5D]' : 'text-gray-600'}`}>
                              {van.rate}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    {selectedVanSize && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs sm:text-sm text-green-800 flex items-center gap-2">
                          <i className="ri-check-circle-fill"></i>
                          <span className="font-medium">
                            {VAN_SIZES.find(v => v.id === selectedVanSize)?.label} selected
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  <p className="text-xs sm:text-sm text-gray-500 text-center mb-6">
                    Van size determines the per-mile rate.
                  </p>

                  {/* Location inputs on same page */}
                  <div className="border-t border-gray-200 pt-6 mt-6">
                    <h3 className="text-lg font-bold text-[#0B3C5D] mb-4">Pickup & Drop-off Locations</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pickup Postcode <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={pickupPostcode}
                          onChange={(e) => setPickupPostcode(e.target.value.toUpperCase())}
                          placeholder="e.g., SW1A 1AA"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pickup Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={pickupAddress}
                          onChange={(e) => setPickupAddress(e.target.value)}
                          placeholder="Full pickup address"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Drop-off Postcode <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={dropoffPostcode}
                          onChange={(e) => setDropoffPostcode(e.target.value.toUpperCase())}
                          placeholder="e.g., E1 6AN (anywhere in UK)"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent text-base"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          <i className="ri-map-pin-line mr-1"></i>
                          Drop-off can be anywhere in the UK
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Drop-off Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={dropoffAddress}
                          onChange={(e) => setDropoffAddress(e.target.value)}
                          placeholder="Full drop-off address (anywhere in UK)"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent text-base"
                        />
                      </div>

                      {isCalculatingDistance && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg animate-pulse">
                          <div className="flex items-center gap-2">
                            <i className="ri-loader-4-line animate-spin text-blue-600 text-xl"></i>
                            <p className="text-blue-800 font-medium">Calculating distance between locations...</p>
                          </div>
                        </div>
                      )}

                      {distance > 0 && !isCalculatingDistance && !distanceError && (
                        <div className="p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-green-800 font-semibold text-lg">
                                ✓ Distance: {distance.toFixed(1)} miles
                              </p>
                              {selectedVanSize && (
                                <p className="text-sm text-green-700 mt-1">
                                  Base mileage cost: £{(distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0)).toFixed(2)}
                                </p>
                              )}
                            </div>
                            <i className="ri-check-circle-fill text-green-600 text-3xl"></i>
                          </div>
                        </div>
                      )}

                      {distanceError && (
                        <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                          <div className="flex items-start gap-2">
                            <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                            <div>
                              <p className="text-red-800 font-medium">Distance Calculation Error</p>
                              <p className="text-red-700 text-sm mt-1">{distanceError}</p>
                              <button
                                onClick={calculateTransportationDistance}
                                disabled={isCalculatingDistance}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium cursor-pointer disabled:opacity-50"
                              >
                                {isCalculatingDistance ? 'Calculating...' : 'Try Again'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Manual Calculate Button - if auto-calc fails */}
                      {!isCalculatingDistance && distance === 0 && !distanceError && pickupPostcode.length >= 5 && dropoffPostcode.length >= 5 && (
                        <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                          <p className="text-blue-800 font-medium mb-3">Ready to calculate distance</p>
                          <button
                            onClick={calculateTransportationDistance}
                            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold cursor-pointer whitespace-nowrap text-base flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                          >
                            <i className="ri-map-pin-distance-line text-xl"></i>
                            Calculate Distance
                          </button>
                        </div>
                      )}

                      {/* Add Another Stop Section */}
                      {distance > 0 && !isCalculatingDistance && selectedVanSize && (
                        <div className="mt-6 space-y-4">
                          {/* Initial Price Display */}
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-bold text-gray-900">Initial Journey Cost</h4>
                              <div className="text-right">
                                <div className="text-sm text-gray-600">Base mileage</div>
                                <div className="text-2xl font-bold text-[#0B3C5D]">
                                  £{(distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0)).toFixed(2)}
                                </div>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">
                              <i className="ri-information-line mr-1"></i>
                              {distance.toFixed(1)} miles × £{VAN_SIZES.find(v => v.id === selectedVanSize)?.rate.toFixed(2)}/mile
                            </div>
                          </div>

                          {/* Add Multi-Drop Button */}
                          <div className="border-2 border-dashed border-[#0B3C5D] rounded-xl p-6 bg-[#0B3C5D]/5">
                            <div className="text-center mb-4">
                              <i className="ri-map-pin-add-line text-4xl text-[#0B3C5D] mb-2"></i>
                              <h4 className="text-lg font-bold text-gray-900 mb-1">Need Multiple Stops?</h4>
                              <p className="text-sm text-gray-600">Add extra drop-off locations to your journey</p>
                            </div>
                            
                            {!showAddStop && additionalStops.length === 0 && (
                              <button
                                onClick={() => setShowAddStop(true)}
                                className="w-full px-6 py-4 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors font-bold cursor-pointer whitespace-nowrap text-base flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                              >
                                <i className="ri-add-circle-line text-xl"></i>
                                Add Multi-Drop (Anywhere in UK)
                              </button>
                            )}

                            {additionalStops.length > 0 && !showAddStop && (
                              <button
                                onClick={() => setShowAddStop(true)}
                                className="w-full px-6 py-3 bg-white border-2 border-[#0B3C5D] text-[#0B3C5D] rounded-lg hover:bg-[#0B3C5D] hover:text-white transition-colors font-bold cursor-pointer whitespace-nowrap text-base flex items-center justify-center gap-2"
                              >
                                <i className="ri-add-circle-line text-xl"></i>
                                Add Another Stop
                              </button>
                            )}

                            {/* Additional Stops List */}
                            {additionalStops.length > 0 && (
                              <div className="space-y-4 mt-4">
                                <h5 className="font-bold text-gray-900 text-sm uppercase tracking-wide">Additional Stops:</h5>
                                {additionalStops.map((stop, index) => (
                                  <div key={stop.id} className="p-4 bg-white border-2 border-gray-200 rounded-lg shadow-sm">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <span className="px-3 py-1 bg-[#0B3C5D] text-white text-xs font-bold rounded-full">
                                            Stop {index + 2}
                                          </span>
                                          <span className="text-sm font-bold text-gray-900">{stop.city}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-2 font-medium">{stop.postcode}</p>
                                        <div className="flex items-center gap-4 text-sm">
                                          <span className="text-gray-700 font-medium">
                                            <i className="ri-map-pin-distance-line mr-1 text-[#0B3C5D]"></i>
                                            +{stop.distance.toFixed(1)} miles
                                          </span>
                                          <span className="text-[#FF6B35] font-bold text-base">
                                            +£{stop.cost.toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => handleRemoveStop(stop.id)}
                                        className="ml-3 p-2 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-all cursor-pointer"
                                      >
                                        <i className="ri-delete-bin-line text-xl"></i>
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add Stop Form */}
                            {showAddStop && (
                              <div className="mt-4 p-5 bg-white border-2 border-[#0B3C5D] rounded-lg space-y-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h5 className="font-bold text-gray-900 text-base">Add New Stop</h5>
                                  <button
                                    onClick={() => {
                                      setShowAddStop(false);
                                      setNewStopPostcode('');
                                      setNewStopCity('');
                                    }}
                                    className="text-gray-500 hover:text-gray-700 cursor-pointer p-1"
                                  >
                                    <i className="ri-close-line text-2xl"></i>
                                  </button>
                                </div>
                                <div>
                                  <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Postcode *
                                  </label>
                                  <input
                                    type="text"
                                    value={newStopPostcode}
                                    onChange={(e) => setNewStopPostcode(e.target.value.toUpperCase())}
                                    placeholder="e.g., M1 1AA (anywhere in UK)"
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-[#0B3C5D] text-base"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    <i className="ri-map-pin-line mr-1"></i>
                                    Any UK postcode accepted
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-bold text-gray-700 mb-2">
                                    City / Location Name *
                                  </label>
                                  <input
                                    type="text"
                                    value={newStopCity}
                                    onChange={(e) => setNewStopCity(e.target.value)}
                                    placeholder="e.g., Manchester, Birmingham, etc."
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-[#0B3C5D] text-base"
                                  />
                                </div>
                                <button
                                  onClick={handleAddStop}
                                  disabled={isCalculatingStop || !newStopPostcode.trim() || !newStopCity.trim()}
                                  className="w-full px-6 py-3 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-base"
                                >
                                  {isCalculatingStop ? (
                                    <span className="flex items-center justify-center gap-2">
                                      <i className="ri-loader-4-line animate-spin"></i>
                                      Calculating Distance...
                                    </span>
                                  ) : (
                                    <span className="flex items-center justify-center gap-2">
                                      <i className="ri-add-line text-xl"></i>
                                      Add This Stop
                                    </span>
                                  )}
                                </button>
                              </div>
                            )}

                            {/* Updated Total Price with All Stops */}
                            {additionalStops.length > 0 && (
                              <div className="mt-5 p-5 bg-gradient-to-br from-[#FF6B35]/10 to-[#FF6B35]/5 border-2 border-[#FF6B35] rounded-xl">
                                <h5 className="font-bold text-gray-900 mb-3 text-base flex items-center gap-2">
                                  <i className="ri-price-tag-3-line text-[#FF6B35]"></i>
                                  UPDATED JOURNEY COST
                                </h5>
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm text-gray-700">
                                    <span>Base journey ({distance.toFixed(1)} miles):</span>
                                    <span className="font-bold">
                                      £{(distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0)).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm text-gray-700">
                                    <span>Additional stops ({additionalStops.length} stop{additionalStops.length > 1 ? 's' : ''}):</span>
                                    <span className="font-bold text-[#FF6B35]">
                                      +£{additionalStops.reduce((sum, stop) => sum + stop.cost, 0).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm text-gray-700 pt-2 border-t-2 border-gray-300">
                                    <span className="font-bold">Total distance:</span>
                                    <span className="font-bold">
                                      {(distance + additionalStops.reduce((sum, stop) => sum + stop.distance, 0)).toFixed(1)} miles
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center pt-3 border-t-2 border-[#FF6B35]">
                                    <span className="font-bold text-gray-900 text-lg">Total Mileage Cost:</span>
                                    <span className="text-2xl font-bold text-[#FF6B35]">
                                      £{(distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0) + 
                                        additionalStops.reduce((sum, stop) => sum + stop.cost, 0)).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                                <div className="mt-3 p-3 bg-white/50 rounded-lg">
                                  <p className="text-xs text-gray-600 text-center">
                                    <i className="ri-information-line mr-1"></i>
                                    This is the mileage cost only. Platform fee, extras, and VAT will be added in the final summary.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 mt-8">
                    <button
                      onClick={() => {
                        setSelectedSubcategory('');
                        setTransportStep(1);
                        setSelectedVanSize('');
                        setPickupPostcode('');
                        setPickupAddress('');
                        setDropoffPostcode('');
                        setDropoffAddress('');
                        setDistance(0);
                        setDistanceError('');
                        setAdditionalStops([]);
                        setError('');
                      }}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Back to Service Selection
                    </button>
                    <button
                      onClick={handleContinueToExtras}
                      className={`flex-1 px-6 py-3 rounded-lg transition-all font-medium cursor-pointer whitespace-nowrap ${
                        isTransportStep2Valid()
                          ? 'bg-[#0B3C5D] text-white hover:bg-[#082A42] shadow-md hover:shadow-lg'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {isCalculatingDistance ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Calculating...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Continue to Extras
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Helper text */}
                  {!isTransportStep2Valid() && (
                    <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                      <p className="text-sm font-medium text-yellow-800 mb-2">
                        <i className="ri-information-line mr-1"></i>
                        Complete these steps to continue:
                      </p>
                      <ul className="text-sm text-yellow-700 space-y-1 ml-5 list-disc">
                        {!selectedVanSize && <li>Select a van size</li>}
                        {pickupPostcode.trim().length < 5 && <li>Enter pickup postcode</li>}
                        {pickupAddress.trim().length < 5 && <li>Enter pickup address</li>}
                        {dropoffPostcode.trim().length < 5 && <li>Enter drop-off postcode</li>}
                        {dropoffAddress.trim().length < 5 && <li>Enter drop-off address</li>}
                        {distance === 0 && !isCalculatingDistance && <li>Wait for distance calculation</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Extras */}
              {transportStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Additional Services</h2>
                    <p className="text-sm text-gray-600">Select any extras you need (optional)</p>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#0B3C5D] transition-all">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={loadingHelp}
                          onChange={(e) => setLoadingHelp(e.target.checked)}
                          className="w-5 h-5 text-[#0B3C5D] rounded focus:ring-[#0B3C5D]"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Loading Help</div>
                          <div className="text-sm text-gray-600">Provider assists with loading items</div>
                        </div>
                      </div>
                      <span className="font-bold text-[#ff751f]">£25</span>
                    </label>

                    <label className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#0B3C5D] transition-all">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={offloadingHelp}
                          onChange={(e) => setOffloadingHelp(e.target.checked)}
                          className="w-5 h-5 text-[#0B3C5D] rounded focus:ring-[#0B3C5D]"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Off-loading Help</div>
                          <div className="text-sm text-gray-600">Provider assists with unloading items</div>
                        </div>
                      </div>
                      <span className="font-bold text-[#ff751f]">£25</span>
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setTransportStep(2)}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setTransportStep(4)}
                      className="flex-1 px-6 py-3 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors font-medium cursor-pointer whitespace-nowrap"
                    >
                      Continue to Summary
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Price Summary */}
              {transportStep === 4 && priceBreakdown && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Price Summary</h2>
                    <p className="text-sm text-gray-600">Review your booking details and total cost</p>
                  </div>

                  {/* Journey Details */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-route-line text-blue-600"></i>
                      Journey Details
                    </h3>
                    
                    {/* First Location */}
                    <div className="bg-white rounded-lg p-4 mb-3 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line text-white"></i>
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-gray-900 mb-1">Pickup Location</div>
                          <div className="text-sm text-gray-600">{pickupAddress}</div>
                          <div className="text-sm font-medium text-gray-700">{pickupPostcode}</div>
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex items-center justify-center my-2">
                      <i className="ri-arrow-down-line text-2xl text-blue-600"></i>
                    </div>

                    {/* First Drop-off with distance and price */}
                    <div className="bg-white rounded-lg p-4 mb-3 shadow-sm border-2 border-blue-200">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-fill text-white"></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-gray-900">Drop-off Location</span>
                            {additionalStops.length > 0 && (
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
                                Multi-Drop
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{dropoffAddress}</div>
                          <div className="text-sm font-medium text-gray-700 mb-2">{dropoffPostcode}</div>
                          <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
                            <div className="flex items-center gap-1 text-sm">
                              <i className="ri-map-pin-distance-line text-blue-600"></i>
                              <span className="font-bold text-gray-900">{distance.toFixed(1)} miles</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <i className="ri-money-pound-circle-line text-green-600"></i>
                              <span className="font-bold text-green-600">
                                £{(distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Stops */}
                    {additionalStops.map((stop, index) => (
                      <div key={stop.id}>
                        <div className="flex items-center justify-center my-2">
                          <i className="ri-arrow-down-line text-2xl text-blue-600"></i>
                        </div>
                        <div className="bg-white rounded-lg p-4 mb-3 shadow-sm border-2 border-orange-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold text-sm">{index + 2}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-gray-900">Stop {index + 2}</span>
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
                                  Multi-Drop
                                </span>
                              </div>
                              <div className="text-sm text-gray-600">{stop.city}</div>
                              <div className="text-sm font-medium text-gray-700 mb-2">{stop.postcode}</div>
                              <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
                                <div className="flex items-center gap-1 text-sm">
                                  <i className="ri-map-pin-distance-line text-blue-600"></i>
                                  <span className="font-bold text-gray-900">+{stop.distance.toFixed(1)} miles</span>
                                </div>
                                <div className="flex items-center gap-1 text-sm">
                                  <i className="ri-money-pound-circle-line text-orange-600"></i>
                                  <span className="font-bold text-orange-600">+£{stop.cost.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Total Journey Stats */}
                    <div className="mt-4 pt-4 border-t-2 border-blue-300">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-900">Total Journey Distance:</span>
                        <span className="text-xl font-bold text-blue-600">
                          {(distance + additionalStops.reduce((sum, stop) => sum + stop.distance, 0)).toFixed(1)} miles
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Cost Breakdown</h3>
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between text-gray-700">
                        <span>Base booking fee</span>
                        <span className="font-semibold">£50.00</span>
                      </div>
                      <div className="flex justify-between text-gray-700">
                        <span>Initial journey ({distance.toFixed(1)} miles × £{VAN_SIZES.find(v => v.id === selectedVanSize)?.rate.toFixed(2)})</span>
                        <span className="font-semibold">£{(distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0)).toFixed(2)}</span>
                      </div>
                      
                      {additionalStops.length > 0 && (
                        <>
                          <div className="flex justify-between text-gray-700 pt-2 border-t border-gray-300">
                            <span className="font-medium">Additional Stops:</span>
                            <span></span>
                          </div>
                          {additionalStops.map((stop, index) => (
                            <div key={stop.id} className="flex justify-between text-gray-700 pl-4">
                              <span>Stop {index + 2} - {stop.city} ({stop.distance.toFixed(1)} miles)</span>
                              <span className="font-semibold text-orange-600">+£{stop.cost.toFixed(2)}</span>
                            </div>
                          ))}
                        </>
                      )}

                      <div className="flex justify-between text-gray-700">
                        <span className="font-medium">Total Mileage Cost</span>
                        <span className="font-semibold">
                          £{(distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0) + 
                            additionalStops.reduce((sum, stop) => sum + stop.cost, 0)).toFixed(2)}
                        </span>
                      </div>

                      {(loadingHelp || offloadingHelp) && (
                        <>
                          <div className="flex justify-between text-gray-700 pt-3 border-t border-gray-300">
                            <span className="font-medium">Extras:</span>
                            <span></span>
                          </div>
                          {loadingHelp && (
                            <div className="flex justify-between text-gray-700 pl-4">
                              <span>Loading help</span>
                              <span className="font-semibold">£25.00</span>
                            </div>
                          )}
                          {offloadingHelp && (
                            <div className="flex justify-between text-gray-700 pl-4">
                              <span>Off-loading help</span>
                              <span className="font-semibold">£25.00</span>
                            </div>
                          )}
                        </>
                      )}
                        
                      <div className="flex justify-between text-gray-700">
                        <span className="font-medium">Subtotal</span>
                        <span className="font-semibold">£{(50 + distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0) + additionalStops.reduce((sum, stop) => sum + stop.cost, 0) + (loadingHelp ? 25 : 0) + (offloadingHelp ? 25 : 0)).toFixed(2)}</span>
                      </div>
                        
                      <div className="flex justify-between text-gray-700">
                        <span className="font-medium">Platform fee (10%)</span>
                        <span className="font-semibold">£{((50 + distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0) + additionalStops.reduce((sum, stop) => sum + stop.cost, 0) + (loadingHelp ? 25 : 0) + (offloadingHelp ? 25 : 0)) * 0.10).toFixed(2)}</span>
                      </div>
                        
                      <div className="flex justify-between text-gray-700">
                        <span className="font-medium">VAT (20%)</span>
                        <span className="font-semibold">£{((50 + distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0) + additionalStops.reduce((sum, stop) => sum + stop.cost, 0) + (loadingHelp ? 25 : 0) + (offloadingHelp ? 25 : 0)) * 1.10 * 0.20).toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {/* Final Total */}
                    <div className="flex justify-between items-center pt-4 border-t-2 border-gray-300">
                      <span className="text-xl font-bold text-gray-900">Final Total</span>
                      <span className="text-2xl font-bold text-[#FF6B35]">£{((50 + distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0) + additionalStops.reduce((sum, stop) => sum + stop.cost, 0) + (loadingHelp ? 25 : 0) + (offloadingHelp ? 25 : 0)) * 1.10 * 1.20).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Contact Details Form */}
                  <div className="border-t border-gray-200 pt-8">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none input-micro input-focus-glow"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none input-micro input-focus-glow"
                          placeholder="john@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none input-micro input-focus-glow"
                          placeholder="+44 7XXX XXXXXX"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          When do you need this service?
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setUrgency('asap')}
                            className={`px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                              urgency === 'asap'
                                ? 'border-[#ff751f] bg-[#ff751f]/5 text-[#ff751f]'
                                : 'border-gray-300 hover:border-[#ff751f]/50'
                            }`}
                          >
                            As Soon As Possible
                          </button>
                          <button
                            type="button"
                            onClick={() => setUrgency('scheduled')}
                            className={`px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                              urgency === 'scheduled'
                                ? 'border-[#ff751f] bg-[#ff751f]/5 text-[#ff751f]'
                                : 'border-gray-300 hover:border-[#ff751f]/50'
                            }`}
                          >
                            Schedule for Later
                          </button>
                        </div>
                      </div>

                      {urgency === 'scheduled' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Preferred Date & Time
                          </label>
                          <input
                            type="datetime-local"
                            id="preferred_date"
                            name="preferred_date"
                            value={scheduledTime}
                            onChange={(e) => setScheduledTime(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none input-micro input-focus-glow cursor-pointer"
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Additional Notes (Optional)
                        </label>
                        <textarea
                          id="details"
                          name="details"
                          rows={4}
                          maxLength={2000}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none resize-none input-micro input-focus-glow"
                          placeholder="Any special requirements or details..."
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          {description.length}/2000 characters
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setTransportStep(3)}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleTransportationCheckout}
                      disabled={proceedingToCheckout || !fullName || !email || !phone}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-all font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {proceedingToCheckout ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Processing...
                        </span>
                      ) : (
                        `Proceed to Checkout (£${((50 + distance * (VAN_SIZES.find(v => v.id === selectedVanSize)?.rate || 0) + additionalStops.reduce((sum, stop) => sum + stop.cost, 0) + (loadingHelp ? 25 : 0) + (offloadingHelp ? 25 : 0)) * 1.10 * 1.20).toFixed(2)} full payment)`
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Regular service form for non-transport categories - Show immediately without subcategory selection */}
          {selectedCategory && selectedCategory !== 'transport-moving' && selectedSubcategory && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none input-micro input-focus-glow"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none input-micro input-focus-glow"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none input-micro input-focus-glow"
                      placeholder="+44 7700 900000"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Service Location</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent"
                      placeholder="123 High Street"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Postcode <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="postcode"
                      name="postcode"
                      required
                      value={formData.postcode}
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value.toUpperCase() })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent"
                      placeholder="SW1A 1AA"
                    />
                  </div>
                </div>
              </div>

              {/* Job Title Field - ONLY for "Other" subcategory */}
              {currentSubcategory?.isOther && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Job Information</h3>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Job Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="job_title"
                      name="job_title"
                      value={formData.jobTitle}
                      onChange={(e) => {
                        if (e.target.value.length <= 60) {
                          setFormData({ ...formData, jobTitle: e.target.value });
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent"
                      placeholder="e.g. Fix loose kitchen cabinet door"
                      maxLength={60}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.jobTitle.length}/60 characters • This helps providers understand your request quickly.
                    </p>
                  </div>
                </div>
              )}

              {/* Scheduling */}
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">When do you need this service?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <button
                    onClick={() => {
                      setFormData({ ...formData, isInstant: true, scheduledDate: '' });
                    }}
                    className={`p-3 sm:p-4 border-2 rounded-xl transition-all cursor-pointer ${
                      formData.isInstant && !formData.scheduledDate
                        ? 'border-[#0B3C5D] bg-[#0B3C5D]/10'
                        : 'border-gray-200 hover:border-[#0B3C5D]'
                    }`}
                  >
                    <i className="ri-time-line text-xl sm:text-2xl text-[#FF6A00] mb-2"></i>
                    <div className="font-bold text-gray-900 text-sm sm:text-base">As Soon As Possible</div>
                    <div className="text-xs sm:text-sm text-gray-600">Get help now</div>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, isInstant: false })}
                    className={`p-3 sm:p-4 border-2 rounded-xl transition-all cursor-pointer ${
                      !formData.isInstant || formData.scheduledDate
                        ? 'border-[#0B3C5D] bg-[#0B3C5D]/10'
                        : 'border-gray-200 hover:border-[#0B3C5D]'
                    }`}
                  >
                    <i className="ri-calendar-line text-xl sm:text-2xl text-[#FF6A00] mb-2"></i>
                    <div className="font-bold text-gray-900 text-sm sm:text-base">Schedule for Later</div>
                    <div className="text-xs sm:text-sm text-gray-600">Choose date & time</div>
                  </button>
                </div>

                {(!formData.isInstant || formData.scheduledDate) && (
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                      <input
                        type="date"
                        id="preferred_date"
                        name="preferred_date"
                        value={formData.scheduledDate}
                        onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent"
                        required={!formData.isInstant}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Time</label>
                      <select
                        id="preferred_time"
                        name="preferred_time"
                        required
                        value={formData.scheduledTime}
                        onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent cursor-pointer"
                      >
                        <option value="">Select time</option>
                        <option value="09:00">09:00</option>
                        <option value="10:00">10:00</option>
                        <option value="11:00">11:00</option>
                        <option value="12:00">12:00</option>
                        <option value="13:00">13:00</option>
                        <option value="14:00">14:00</option>
                        <option value="15:00">15:00</option>
                        <option value="16:00">16:00</option>
                        <option value="17:00">17:00</option>
                        <option value="18:00">18:00</option>
                        <option value="19:00">19:00</option>
                        <option value="20:00">20:00</option>
                        <option value="21:00">21:00</option>
                        <option value="22:00">22:00</option>
                        <option value="23:00">23:00</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {currentSubcategory?.isOther ? 'Describe the Job' : 'Job Description'} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  maxLength={2000}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent"
                  placeholder={currentSubcategory?.isOther ? "Tell us what you need, sizes, access info, urgency..." : "Please provide any additional details about your job..."}
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.description.length}/2000 characters</p>
                {currentSubcategory?.isOther && (
                  <p className="text-xs text-[#FF6B35] mt-2">
                    <i className="ri-information-line"></i> Since you selected "Other", please provide detailed information about the service you need.
                  </p>
                )}
              </div>

              {/* Photo Upload */}
              <PhotoUploadField 
                onPhotosChange={setUploadedPhotos}
                maxPhotos={10}
              />

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !isPostcodeValid}
                className="w-full py-3 sm:py-4 bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D] text-white rounded-lg hover:from-[#082A42] hover:to-[#082A42] transition-all duration-300 font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer btn-micro btn-ripple"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  'Submit Request'
                )}
              </button>

              <p className="text-center text-xs sm:text-sm text-gray-600 mt-3 sm:mt-4">
                No payment required now. You'll only pay after the job is completed.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ServiceRequestPage;
